import { createContext, use, useEffect, useState } from 'react'
import { storage } from './storage'

const Context = createContext()

export function Auth({ children }) {
  const [auth, setAuth] = useState(null)
  useEffect(() => {
    async function init() {
      const ref = {
        store: null,
      }

      const api = {
        async get(path) {
          const headers = {
            'Content-Type': 'application/json',
            'X-Token': ref.store?.token || '',
            'X-GM': ref.store?.gm || '',
            'X-GMU': ref.store?.gmu || '',
          }
          const resp = await fetch(`/api${path}`, {
            method: 'GET',
            headers,
          })
          const data = await resp.json()
          if (data.err) throw { code: data.err }
          return data
        },

        async post(path, body) {
          const headers = {
            'Content-Type': 'application/json',
            'X-Token': ref.store?.token || '',
            'X-GM': ref.store?.gm || '',
            'X-GMU': ref.store?.gmu || '',
          }
          const resp = await fetch(`/api${path}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
          })
          const data = await resp.json()
          if (data.err) throw { code: data.err }
          return data
        },

        stream(path, emit, onEnd) {
          const headers = {
            'Content-Type': 'application/json',
            'X-Token': ref.store?.token || '',
            'X-GM': ref.store?.gm || '',
            'X-GMU': ref.store?.gmu || '',
          }
          const controller = new AbortController()
          let dead = false

          async function run() {
            try {
              const resp = await fetch(`/api${path}`, {
                method: 'GET',
                headers,
                signal: controller.signal,
              })
              if (resp.status === 404) {
                return onEnd('not_found')
              }
              const reader = resp.body.getReader()
              const decoder = new TextDecoder()
              let buffer = ''
              while (true) {
                const { done, value } = await reader.read()
                if (done) break
                buffer += decoder.decode(value, { stream: true })
                const lines = buffer.split('\n')
                buffer = lines.pop()
                for (const line of lines) {
                  if (line.trim()) {
                    emit(JSON.parse(line))
                  }
                }
              }
              onEnd?.()
            } catch (err) {
              if (err.name !== 'AbortError') {
                onEnd?.(err)
              }
            }
          }
          run()

          return () => {
            if (dead) return
            dead = true
            controller.abort()
          }
        },

        ws(path) {
          const url = `/api${path}?token=${ref.store.token}`
          return new WebSocket(url)
        },
      }

      updateAuth()

      function updateAuth() {
        if (ref.store?.user) {
          setAuth({
            user: ref.store.user,
            setGMU(value) {
              setGMU(value)
            },
            ...api,
          })
        } else {
          setAuth(null)
        }
      }

      async function setGMU(value) {
        ref.store.gmu = value
        const user = await api.get('/user')
        ref.store.user = user
        storage.set('auth', ref.store)
        updateAuth()
      }

      async function createGuest() {
        ref.store = await api.post('/users')
        storage.set('auth', ref.store)
        updateAuth()
      }

      async function checkAuth() {
        try {
          const user = await api.get('/user')
          ref.store.user = user
          storage.set('auth', ref.store)
          updateAuth()
        } catch (err) {
          if (err.code === 'not_authorized') {
            ref.store.user = null
            updateAuth()
            await createGuest()
          }
        }
      }

      ref.store = storage.get('auth') // { user, token, gm, gmu }
      if (ref.store) {
        updateAuth()
        await checkAuth()
      } else {
        await createGuest()
      }
    }
    init()
  }, [])
  if (!auth) return null
  return <Context.Provider value={auth}>{children}</Context.Provider>
}

export function useAuth() {
  return use(Context)
}
